import { useEffect, useState } from 'react';

import { isEmpty, isNil, omit } from 'ramda';
import { useForm, useFormState } from 'react-final-form';

import {
  IAdditionalInfoProps,
  IArrayFieldsProps,
  IBlockProps,
  IConfigProps,
  IFieldsDataProps,
} from 'components/shared/ArrayFields/types';
import generateRandomString from 'utils/generateRandomString';
import { updateArrayBy } from 'utils/immutableFunctions';

const localIdKey = '_localId';
const isRemoteBlock = (block: IBlockProps) => !isNil(block.id);
const isLocalBlock = (block: IBlockProps) => !isNil(block[localIdKey]);

const createBlockItem = (fieldsData: Omit<IFieldsDataProps, 'label' | 'autoComplete' | 'onFocus'>[]) => {
  const initialBlockState = fieldsData.reduce((acc, item) => ({ ...acc, [item.name]: null }), {});
  return { [localIdKey]: generateRandomString(6), ...initialBlockState };
};

const initializeBlocks = (
  initialBlocks: IAdditionalInfoProps[],
  fieldsData: IFieldsDataProps[],
  { initWithEmptyBlock = false }: IConfigProps = {},
) => {
  if (!isNil(initialBlocks) && !isEmpty(initialBlocks)) {
    return initialBlocks;
  }

  return initWithEmptyBlock ? [createBlockItem(fieldsData)] : [];
};

export const useHanlders = ({ initialValues, data }: IArrayFieldsProps) => {
  const form = useForm();
  const { values } = useFormState();
  const { sectionName, fieldsData, config = {} } = data;

  const { initWithEmptyBlock = true } = config;
  const [blocks, setBlocks] = useState<IBlockProps[]>([]);

  const findBlockIndexRegisteredInForm = (block: IBlockProps) => {
    if (isNil(values[sectionName])) {
      return -1;
    }

    const idKey = isRemoteBlock(block) ? 'id' : localIdKey;
    return values[sectionName].findIndex((item: IAdditionalInfoProps) => item[idKey] === block[idKey]);
  };

  const isBlockEmpty = (block: IBlockProps) => {
    return fieldsData.every(({ name }) => isNil(block[name]));
  };

  const cleanUpBlock = (block: IBlockProps) =>
    omit(
      fieldsData.map((v) => v.name),
      block,
    );

  useEffect(() => {
    // @ts-ignore
    setBlocks(initializeBlocks(initialValues[sectionName], fieldsData, { initWithEmptyBlock }));
  }, [initialValues]);

  const formHanlders = {
    deleteRemoteBlock: (blockToDelete: IBlockProps) => {
      if (isNil(values[sectionName])) return;

      form.change(
        sectionName,
        updateArrayBy(
          values[sectionName],
          (block: IBlockProps) => block.id === blockToDelete.id,
          () => ({ ...cleanUpBlock(blockToDelete), _destroy: true }),
        ),
      );
    },

    deleteLocalBlock: (blockToDelete: IBlockProps) => {
      if (isNil(values[sectionName])) return;

      form.change(
        sectionName,
        values[sectionName].filter((block: IBlockProps) => block[localIdKey] !== blockToDelete[localIdKey]),
      );
    },
    updateBlock: (newBlock: IBlockProps) => {
      if (isNil(values[sectionName])) return;

      const idKey = isRemoteBlock(newBlock) ? 'id' : localIdKey;

      form.change(
        sectionName,
        updateArrayBy(
          values[sectionName],
          (block: IBlockProps) => block[idKey] === newBlock[idKey],
          () => newBlock,
        ),
      );
    },

    appendBlock: (newBlock: IBlockProps) => {
      if (isNil(values[sectionName])) {
        form.change(sectionName, [newBlock]);
        return;
      }

      form.change(sectionName, [...values[sectionName], newBlock]);
    },
  };

  const createBlock = () => {
    //@ts-ignore
    setBlocks((blocks) => [...blocks, createBlockItem(fieldsData)]);
  };

  const deleteBlock = (blockToDelete: IBlockProps) => {
    if (isRemoteBlock(blockToDelete)) {
      formHanlders.deleteRemoteBlock(blockToDelete);

      setBlocks((blocks) => {
        return blocks.filter((block) => block.id !== blockToDelete.id);
      });
    }
    if (isLocalBlock(blockToDelete)) {
      formHanlders.deleteLocalBlock(blockToDelete);
      setBlocks((blocks) => blocks.filter((block) => block[localIdKey] !== blockToDelete[localIdKey]));
    }
  };

  const updateValue = (block: IBlockProps, fieldName: string, value: string) => {
    setBlocks((blocks) => {
      const newBlock = { ...block, [fieldName]: value || null };
      if (isBlockEmpty(newBlock)) {
        if (isRemoteBlock(newBlock)) {
          formHanlders.deleteRemoteBlock(newBlock);
        }
        if (isLocalBlock(newBlock)) {
          formHanlders.deleteLocalBlock(newBlock);
        }
      } else {
        if (isRemoteBlock(newBlock)) {
          formHanlders.updateBlock(newBlock);
        }
        if (isLocalBlock(newBlock)) {
          const isExist = values[sectionName]?.find((block: IBlockProps) => block[localIdKey] === newBlock[localIdKey]);

          if (isExist) {
            formHanlders.updateBlock(newBlock);
          } else {
            formHanlders.appendBlock(newBlock);
          }
        }
      }

      const idKey = isRemoteBlock(newBlock) ? 'id' : localIdKey;
      return updateArrayBy(
        blocks,
        (block: IBlockProps) => block[idKey] === newBlock[idKey],
        () => newBlock,
      );
    });
  };

  return {
    updateValue,
    deleteBlock,
    createBlock,
    blocks,
    findBlockIndexRegisteredInForm,
    localIdKey,
  };
};
